import React, { useEffect } from "react";
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { withResizeDetector } from 'react-resize-detector';

import { hasReactHookFormError } from 'components/base/ErrorFieldMessage';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';
import CustomRadios from "components/base/CustomRadios";
import BaseMultipleSelectorV2 from 'components/base/BaseMultipleSelectorV2';
import LookupWithMutipleNumber from 'components/segment/LookupWithMutipleNumber';
import InputWithMutipleDropDown from 'components/segment/InputWithMutipleDropDown';
import { levelSoonExpireSegmentTypes, levelSoonExpireSegmentOptions } from 'models/SegmentModel'
import { PERIOD_TYPE_DISPLAY } from 'config/CustomEnums'

const EXPIRE_DATE_TYPE_OPTIONS = [
  PERIOD_TYPE_DISPLAY.WITHIN,
  PERIOD_TYPE_DISPLAY.EQUAL_TO,
  PERIOD_TYPE_DISPLAY.BETWEEN,
  PERIOD_TYPE_DISPLAY.AFTER,
  PERIOD_TYPE_DISPLAY.BEFORE,
]

const LevelSelector = ({
  title,
  fieldBaseName
}) => {
  const {
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext();
  const levels = useSelector(state => state.levels.levelList)
  const targetFieldName = `${fieldBaseName}.selectedLevels`;
  const selectedLevels = watch(targetFieldName);

  return (
    <Controller
      control={control}
      name={targetFieldName}
      shouldUnregister
      rules={{ required: 'Please select at least one item' }}
      render={({ field }) => (
        <BaseMultipleSelectorV2
          title={title}
          data={{
            sourceData: levels,
            targetData: selectedLevels || [],
            targetChange: (options) => {
              setValue(field.name, options, { shouldDirty: true, shouldValidate: true });
            },
          }}
          sourceReminderText={'Please select item(s)'}
          amidReminderText={'Please select item(s)'}
          error={{
            error: hasReactHookFormError(errors, field.name),
            message: 'Please select at least one item',
          }}
        />
      )}
    />
  )
}
const LevelSelectorWithWindowDetector = withResizeDetector(LevelSelector);


const RemainingNumberOfDays = ({ fieldBaseName }) => {
  const { watch } = useFormContext();
  const expireTypeFieldName = `${fieldBaseName}.expireType`;
  const watchExpireType = watch(expireTypeFieldName);
  if (watchExpireType !== levelSoonExpireSegmentTypes.REMAINING_DAYS) {
    return null
  }
  return (
    <LookupWithMutipleNumber
      fieldBaseName={fieldBaseName}
      groupName={"signUpAnniversaryCharacteristic"}
      title={'Remaining number of days'}
      extraSuffix={"days"}
      notRequired
    />
  )
}

const ExpireDateTime = ({
  fieldBaseName
}) => {
  const {
    watch
  } = useFormContext();
  const expireTypeFieldName = `${fieldBaseName}.expireType`;
  const watchExpireType = watch(expireTypeFieldName);
  if (watchExpireType !== levelSoonExpireSegmentTypes.EXPIRY_DATETIME) {
    return null
  }
  return (
    <InputWithMutipleDropDown
      fieldBaseName={fieldBaseName}
      title={"Expiry datetime"}
      rangeOptions={EXPIRE_DATE_TYPE_OPTIONS}
      hasPeriodDropdown
      requireFutureTime
    />
  )
}
const resetFields = (setValue, fieldBaseName, suffixes) => {
  suffixes.forEach(suffix => {
    const fieldName = `${fieldBaseName}.${suffix}`;
    setValue(fieldName, null, { shouldDirty: true });
  });
};

const resetRemainingDays = (setValue, fieldBaseName) => {
  resetFields(setValue, fieldBaseName, ['lookup', 'minNum', 'maxNum']);
}

const resetExpiryDatetime = (setValue, fieldBaseName) => {
  resetFields(setValue, fieldBaseName, ['periodRange', 'minDate', 'maxDate', 'numberOfDays']);
}

const LevelExpireChoice = ({
  fieldBaseName
}) => {
  const {
    watch,
    setValue,
    control
  } = useFormContext();
  const expireTypeFieldName = `${fieldBaseName}.expireType`;
  const watchExpireType = watch(expireTypeFieldName);
  useEffect(() => {
    if (!watchExpireType) {
      setValue(expireTypeFieldName, levelSoonExpireSegmentTypes.REMAINING_DAYS, { shouldDirty: true });
      return
    }
    if (watchExpireType === levelSoonExpireSegmentTypes.EXPIRY_DATETIME) {
      resetRemainingDays(setValue, fieldBaseName)
      return
    }
    if (watchExpireType === levelSoonExpireSegmentTypes.REMAINING_DAYS) {
      resetExpiryDatetime(setValue, fieldBaseName)
    }
  }, [watchExpireType])
  return (
    <>
      <CustomTitleLabel title={"By"} />
      <Controller
        name={expireTypeFieldName}
        control={control}
        render={({ field }) => (
          <CustomRadios
            onChange={(value) => {
              setValue(field.name, value, { shouldDirty: true });
            }}
            options={levelSoonExpireSegmentOptions}
            default={watchExpireType}
          />
        )}
      />
    </>
  )
}


const LevelSoonExpireSection = ({
  title,
  fieldBaseName
}) => {
  return (
    <div className="d-flex flex-column">
      <LevelSelectorWithWindowDetector title={title} fieldBaseName={fieldBaseName} />
      <LevelExpireChoice fieldBaseName={fieldBaseName} />
      <RemainingNumberOfDays fieldBaseName={fieldBaseName} />
      <ExpireDateTime fieldBaseName={fieldBaseName} />
    </div>
  )
}

export default LevelSoonExpireSection;